import dayjs from 'dayjs'
import useDefaultDevice from 'hooks/useDefaultDevice/useDefaultDevice'
import useReceiptLine from 'hooks/useReceiptLine/useReceiptLine'
import { isEmpty, isNil, isNull, sortBy } from 'lodash-es'
import {
    BindingOrderLine,
    DiscountRedemptionDetailContract,
    ModifierOptionType,
    PrinterContract,
    ReceiptBindingDataContract,
    SalePaymentInfoContract,
    BindingSaleItem,
    BindingCompModel,
    PaymentStatus,
    OrderDiscountType,
    OrderDiscountContract,
    BindingOrderLineDiscount,
    BindingOrderModel,
    SaleItemState,
    PrinterType,
    PrinterMode,
} from 'models'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormat } from 'Services/localize/format'
import { useAppSelector } from 'store/useStoreHook'
import nameof from 'utilities/nameof'
import { PAYMENT_RECEIVE_STATUS } from 'components/Sell/utils/types'
import { SaleItemType } from 'models'
import { ComboChoiceType } from 'models'
import { PAYMENT_TYPE_ID } from 'constant/paymentType'
import localeData from 'dayjs/plugin/localeData'
import { ReceiptTemplateType } from 'constant/receiptTemplateType'
import { sendPrintCommandToAndroidPrintingApp } from 'utilities/androidPrinting'
import { OrderFulfilmentType } from 'store/models/sellModels'
import { roundNumber } from 'utilities/format'
import { isOpenItemId } from 'constant/magicNumber'
import { RECEIVE_PAYMENT_STATUS } from 'components/Sell/payment/dialogs/splitPaymentDialog/SplitPayment'
import useCurrentTerminalDevicesHwAgentCaller from 'hooks/useHardwareAgentCaller/useCurrentTerminalDevicesHwAgentCaller'
import Guid from 'utilities/Guid'
import tableService from 'Services/api/table.service'
import useTableLayoutDisplay from 'hooks/useTableLayoutDisplay/useTableLayoutDisplay'

dayjs.extend(localeData)
interface IOrderwireMarkup {
    template: string
    label?: string
    value: string
    section: string
}
interface SalePaymentWithChange extends SalePaymentInfoContract {
    changeAmount: number
}
const mapPaymentTypeName = (paymentTypeId?: number, customName?: string) => {
    if (!paymentTypeId) return 'Payment'
    if (customName) return customName
    if (paymentTypeId === PAYMENT_TYPE_ID.CASH) return 'Cash'
    if (
        paymentTypeId === PAYMENT_TYPE_ID.CUSTOMER_ACCOUNT ||
        paymentTypeId === PAYMENT_TYPE_ID.CHANNEL_ACCOUNT
    )
        return 'Account'
    if (paymentTypeId === PAYMENT_TYPE_ID.PROMPTPAY) return 'Prompt Pay'
    if (paymentTypeId === PAYMENT_TYPE_ID.GIFTCARD) return 'Gift Card'
    if (paymentTypeId === PAYMENT_TYPE_ID.CREDITCARD) return 'Credit Card'
    if (paymentTypeId === PAYMENT_TYPE_ID.EVO_PAYMENT) return 'EVO'
    return 'Payment'
}

const paymentMethodHeader = (headerLabel: string, paymentTypeName?: string) => {
    let rtl: string[] = []
    if (paymentTypeName) {
        rtl = ['{width: * *}', `|${headerLabel}: | ${paymentTypeName?.trim()}`]
    } else {
        rtl = ['{width: *}', `|${headerLabel}`, '{width: * 9}']
    }
    return rtl.join('\n')
}
const removeFieldFromTemplate = (markupPerLine: string[], fields: string[]) => {
    const result = markupPerLine.filter(
        (i) => !fields.some((f) => i.includes(f)),
    )
    return result
}

/**
 * Group up the payment that have same payment type, payment account, giftcard
 * will not group up the cash payment since it has change amount
 * @param payments
 * @returns
 */
const mergeSamePaymentDetails = (
    payments: SalePaymentInfoContract[],
    isSplitChange: boolean = true,
) => {
    return sortBy(payments, (a) => [
        RECEIVE_PAYMENT_STATUS.includes(a.status!) ? -1 : 1,
        a.paymentTypeID,
        a.paymentAccountName,
    ])
        .reduce<SalePaymentWithChange[]>((acc, curr) => {
            const p = acc.find(
                (f) =>
                    f.paymentID === curr.referencePaymentID ||
                    ((f.paymentTypeID !== PAYMENT_TYPE_ID.CASH ||
                        !isSplitChange ||
                        f.status === PaymentStatus.Refunded) &&
                        f.paymentTypeID === curr.paymentTypeID &&
                        f.customPaymentTypeID === curr.customPaymentTypeID &&
                        f.paymentAccountName === curr.paymentAccountName &&
                        f.transactionID === curr.transactionID),
            )
            if (!p) {
                acc.push({ ...curr, changeAmount: 0 })
            } else if (
                curr.status === PaymentStatus.Returned ||
                curr.status === PaymentStatus.Cancelled
            ) {
                p.changeAmount += curr.amount ?? 0
            } else if (p.amount) {
                p.amount += curr.amount ?? 0
            }
            return acc
        }, [])
        .filter((n) => n.amount! > 0)
}

/**
 * receipt template type id that need bigger font
 */
const shouldBiggerDetail = (type: number | undefined) =>
    [ReceiptTemplateType.Kitchen].includes(type || 0)

const adjustBiggerFont = (line: string, isBiggerDetail: boolean) => {
    if (!isBiggerDetail) return line

    // Ignore notes lines
    if (line.includes('~"Note')) return line
    // Ignore seat note lines
    if (line.startsWith('Seat:')) return line

    const biggerSign = '^^^'
    const indentSign = /~+/g
    const columnSign = /\|/g
    const columnWithInvertMark = /\|\^+(\s*)`(.+\s*)/g
    const lastBigSign = /\^+ *\n*$/g
    //add ^ after indent sign
    if (line.includes('~')) line = line.replace(indentSign, `$&${biggerSign}`)
    else line = biggerSign + line
    const result = line
        //add ^ after column sign
        .replace(columnSign, `$&${biggerSign}`)
        .replace(columnWithInvertMark, `|\`${biggerSign}$1$2`)
        .replace(lastBigSign, '')

    return result
}
const resizeImage = (
    imageBase64: string,
    maxWidth: number,
): Promise<string> => {
    return new Promise((resolve) => {
        if (!imageBase64 || imageBase64 === '') {
            resolve('')
            return
        }
        if (typeof maxWidth === 'undefined') maxWidth = 100
        const img = new Image()
        img.src = imageBase64
        const width = maxWidth
        let height = maxWidth
        const ratio = maxWidth / width
        height *= ratio
        img.onload = function () {
            const canvas = document.createElement('canvas')
            const context = canvas.getContext('2d')
            canvas.width = width
            canvas.height = height
            context!.fillStyle = '#FFFFFF' // Add bg color
            context!.fillRect(0, 0, canvas.width, canvas.height)
            context!.drawImage(img, 0, 0, width, height)
            resolve(canvas.toDataURL('image/png', 1))
        }
    })
}
const getValue = (line: string, attr: string = ORDERWIRE_MARKUP.value) => {
    const value = line
        .replaceAll(`${attr}:`, '')
        .replaceAll(/(['"])/g, '')
        .trim()
    return value
}
interface ITemplateData {
    markup: string
    data: ReceiptBindingDataContract
    receiptTemplateType?: number
    isDuplicateReceipt?: boolean
}

interface PrintPreviewOption {
    isAndroid?: boolean
    tableId?: string
}

interface ParseSaleDetailProps {
    isExtendDetail: boolean
    bindOrderLines: BindingOrderLine[]
    showPrice: boolean
    isShowNotes: boolean
    hideHeader: boolean
    isGroupByCategory: boolean
    useSecondaryTextStyle: boolean
    receiptTemplateType?: number
}

const splitter = '\n'

const ORDERWIRE_MARKUP = nameof<IOrderwireMarkup>()

const useReceiptTemplate = () => {
    const { displaySeatNote } = useTableLayoutDisplay()

    const companyLogo = useAppSelector((state) => state.operation.logo)
    const isOperationLoading = useAppSelector(
        (state) => state.operation.loading,
    )
    const kitchenCategorySortOrderMap = useAppSelector(
        (state) => state.operation.kitchenCategorySortOrderMap,
    )

    type ICategorySortable = { categoryID?: string | null }
    const kitchenCategorySortFunction = useCallback(
        (a: ICategorySortable, b: ICategorySortable) => {
            let aSortOrder = Number.MAX_SAFE_INTEGER
            let bSortOrder = Number.MAX_SAFE_INTEGER
            if (
                a.categoryID !== null &&
                a.categoryID !== undefined &&
                kitchenCategorySortOrderMap[a.categoryID]?.sortIndex !== null &&
                kitchenCategorySortOrderMap[a.categoryID]?.sortIndex !==
                    undefined
            ) {
                aSortOrder = kitchenCategorySortOrderMap[a.categoryID]
                    .sortIndex!
            }
            if (
                b.categoryID !== null &&
                b.categoryID !== undefined &&
                kitchenCategorySortOrderMap[b.categoryID]?.sortIndex !== null &&
                kitchenCategorySortOrderMap[b.categoryID]?.sortIndex !==
                    undefined
            ) {
                bSortOrder = kitchenCategorySortOrderMap[b.categoryID]
                    .sortIndex!
            }
            return aSortOrder - bSortOrder
        },
        [kitchenCategorySortOrderMap],
    )

    const [companyLogoExtractedType, setCompanyLogoExtractedType] = useState<
        string
    >()
    const [
        companyLogoExtractedTypeLoading,
        setCompanyLogoExtractedTypeLoading,
    ] = useState<boolean>(true)

    useEffect(() => {
        if (
            companyLogoExtractedTypeLoading &&
            !isOperationLoading &&
            companyLogo
        ) {
            resizeImage(companyLogo, 100)
                .then((result) => {
                    setCompanyLogoExtractedType(
                        result.replace(/^data:image\/[a-z]+;base64,/, ''),
                    )
                })
                .finally(() => {
                    setCompanyLogoExtractedTypeLoading(false)
                })
        }
    }, [companyLogo, companyLogoExtractedTypeLoading, isOperationLoading])

    const [previewSvg, setPreview] = useState<string>('')
    const [parsing, setParsing] = useState<boolean>(false)
    const [printing, setPrinting] = useState<boolean>(false)

    const hardwareAgent = useCurrentTerminalDevicesHwAgentCaller()

    const {
        enableOnScreenPrinter,
        enableAndroidPrinting,
        onScreenPrinter,
        defaultPrinter,
    } = useDefaultDevice()

    const isRemoveBordersAndLines = useMemo(
        () =>
            // Remove borders and horizontal lines - as some printers do not support them.
            // Except the case that printer mode is set to "Generic" - as we might able to keep using the border
            defaultPrinter?.printerType === PrinterType.Usb &&
            defaultPrinter?.printerMode !== PrinterMode.Generic,
        [defaultPrinter?.printerMode, defaultPrinter?.printerType],
    )

    const {
        transformReceiptCommand,
        transformReceiptSvgBase64,
    } = useReceiptLine({
        onScreenPrinter,
        defaultPrinter,
    })

    const {
        currencyCode: systemCurrencyCode,
        shortDateFormat,
        time,
        dateTimeFormat,
        currencySymbol,
        decimalFormat,
        currency,
    } = useFormat()

    const formatDecimal = useCallback(
        (value: number | undefined) => {
            return decimalFormat(value ?? 0)
        },
        [decimalFormat],
    )

    const formatNullableString = useCallback(
        (value: string | null | undefined, placeholder: string = '\\-') => {
            return `${
                value?.trim()?.replaceAll('_', '\\_')?.replaceAll('-', '\\-') ||
                placeholder
            }`
        },
        [],
    )
    const parseDiscountDetail = useCallback(
        (data: ReceiptBindingDataContract) => {
            const currencyCode =
                data.paymentCurrency?.code || systemCurrencyCode || 'USD'

            const getDiscountName = (
                discount: OrderDiscountContract | BindingOrderLineDiscount,
            ) =>
                discount.discountID
                    ? `${discount.discountName}(${
                          discount.isPercentageDiscount
                              ? `${discount.discountValue}%`
                              : `${currency(
                                    discount.discountValue ?? 0,
                                    currencyCode,
                                )}`
                      })`
                    : discount.isPercentageDiscount
                    ? `${discount.discountValue}% OFF`
                    : `${currency(
                          discount.discountValue ?? 0,
                          currencyCode,
                      )} OFF`

            const orderDiscounts =
                data.order?.orderDiscounts
                    ?.filter(
                        (d) =>
                            d.orderDiscountType !==
                            OrderDiscountType.RedeemPoint,
                    )
                    .map((ld) => {
                        return {
                            name: getDiscountName(ld),
                            amount: ld.discountAmount ?? 0,
                        }
                    }) ?? []
            const lineDiscounts =
                data.order?.orderLines?.flatMap((l) => {
                    return (
                        l.orderLineDiscounts?.map((ld) => {
                            return {
                                name: `${getDiscountName(ld)} @${l.itemName}`,
                                amount: ld.discountAmount ?? 0,
                            }
                        }) ?? []
                    )
                }) ?? []

            if (lineDiscounts.length > 0 || orderDiscounts.length > 0) {
                let rtl = '{width:25 *}'
                lineDiscounts.concat(orderDiscounts).forEach((l) => {
                    rtl += `\n~${l.name} | ${formatDecimal(l.amount * -1)}|`
                })
                return rtl
            }
            return ''
        },
        [currency, formatDecimal, systemCurrencyCode],
    )
    const getBindingData = useCallback(
        (field: string, data: ReceiptBindingDataContract) => {
            if (field.includes('LOCATION_NAME')) {
                return formatNullableString(data.location?.name)
            } else if (field.includes('LOCATION_ADDRESS')) {
                return `${formatNullableString(
                    data.location?.address?.addressLine1,
                    '',
                )}\n${formatNullableString(
                    data.location?.address?.addressLine2,
                    '',
                )}`
            } else if (field.includes('LOCATION_PHONE')) {
                return `Phone: ${formatNullableString(
                    data.location?.companyPhone,
                )}`
            } else if (field.includes('ORDER_DATE')) {
                return `${shortDateFormat(data.order?.orderDateTime)}`
            } else if (field.includes('ORDER_TIME'))
                return `${time(data.order?.orderDateTime, false)}`
            else if (field.includes('EXPECTED_DATE_TIME'))
                return data.order?.pickUpDateTime
                    ? formatNullableString(
                          dateTimeFormat(data.order?.pickUpDateTime, false),
                      )
                    : 'ASAP'
            else if (field.includes('ORDER_POS_ID'))
                return formatNullableString(data.posSerialNo)
            else if (field.includes('ORDER_CUSTOMER'))
                return formatNullableString(data.customer?.givenName)
            else if (field.includes('ORDER_NUMBER'))
                return formatNullableString(data.order?.orderNo)
            else if (field.includes('RECEIPT_NUMBER'))
                return formatNullableString(data.saleNo)
            else if (field.includes('SUB_TOTAL'))
                return formatDecimal(data.subtotal)
            else if (field.includes('SERVICE_CHARGES'))
                return formatDecimal(
                    (data.serviceChargeAmount ?? 0) +
                        (data.order?.additionalChargeAmount ?? 0),
                )
            else if (field.includes('ORDER_SERVER'))
                return formatNullableString(data.order?.serverName)
            else if (field.includes('ORDER_STAFF'))
                return formatNullableString(data.staffName)
            else if (field.includes('DISCOUNTS')) {
                let discount = formatDecimal((data.discountAmount ?? 0) * -1)
                if (field.includes('+details'))
                    discount += `\n${parseDiscountDetail(data)}`
                return discount
            } else if (field.includes('TAX'))
                return formatDecimal(data.totalTaxAmount)
            else if (field.includes('TIP_AMOUNT'))
                return formatDecimal(data.tipAmount)
            else if (field === 'TOTAL')
                return formatDecimal(data.totalDueAmount)
            else if (field.includes('BALANCE_TOTAL')) {
                // Only add bold styling (") if no sale has been made yet (i.e. bill preview.)
                const styling = `${!data.saleNo ? '"' : ''}`
                return `${styling}${formatDecimal(
                    data.balanceDueAmount,
                )}${styling}`
            } else if (field.includes('PAYMENT_AMOUNT'))
                return formatDecimal(data.tenderedAmount)
            else if (field.includes('PAYMENT_CHANGE'))
                return formatDecimal(data.changeAmount)
            else if (field.includes('PRINT_ORDER_BARCODE'))
                return formatNullableString(data.order?.orderNo)
            else if (field.includes('ORDER_VAT_ID'))
                return formatNullableString(data.location?.vatid)
            else if (field.includes('COMPANY_REGISTRATION_ID'))
                return formatNullableString(
                    data.location?.companyRegistrationId,
                )
            else if (field.includes('ORDER_CHANNEL'))
                return formatNullableString(data.order?.originChannelName)
            else if (field.includes('COLLECTION_CODE'))
                return formatNullableString(data.order?.collectionCode)
            else if (field.includes('ONLINE_STORE'))
                return formatNullableString(data.order?.onlineStoreName)
            else if (field.includes('FULFILLMENT'))
                return formatNullableString(
                    data.order?.orderFulfillmentType?.name,
                )
            else if (field.includes('TABLE_NUMBER'))
                return formatNullableString(data.order?.tableName)
            else if (field.includes('SEAT_NUMBER'))
                return formatNullableString(data.seatNumber)
            else if (field.includes('SEAT_COUNT'))
                return formatNullableString(data.order?.guestCount?.toString())
            else if (field.includes('POINT_REDEEM_DISCOUNT'))
                return formatDecimal((data.pointRedeemDiscount ?? 0) * -1)
            else if (field.includes('TOTAL_REFUNDED'))
                return formatDecimal(data.totalRefunded ?? 0)
            else if (field.includes('ORDER_RECEIVED'))
                return data.order?.orderDateTime
                    ? dateTimeFormat(data.order?.orderDateTime, false)
                    : '-'
            else if (field.includes('DELIVERY_ORDER_NUMBER'))
                return formatNullableString(data.order?.orderNo)
            else if (field.includes('DELIVERY_FEE'))
                return formatDecimal(data.order?.deliveryChargeAmount)
            else return field
        },
        [
            formatNullableString,
            shortDateFormat,
            time,
            dateTimeFormat,
            formatDecimal,
            parseDiscountDetail,
        ],
    )

    const parseTax = useCallback(
        (data: ReceiptBindingDataContract) => {
            let rlt = ''
            data.taxComponents?.forEach((tax, index) => {
                let newLine = `${tax.name} (${tax.rate}%)`
                const hasTaxInclusive =
                    tax.taxInclusiveAmount && tax.taxInclusiveAmount > 0

                if (hasTaxInclusive) {
                    // Taxes are all included
                    if (tax.taxInclusiveAmount === tax.amount)
                        newLine = `{w: * 13}\n ${newLine} (INCL.) | ${formatDecimal(
                            tax.amount,
                        )}`
                    else
                        newLine = `{w: auto}\n ${newLine} | ${formatDecimal(
                            tax.amount,
                        )} (${tax.taxInclusiveAmount} INCL.)`
                } else {
                    newLine = `{w: * 13}\n ${newLine} | ${formatDecimal(
                        tax.amount,
                    )}`
                }

                if (index + 1 < data.taxComponents?.length!!) newLine += '\n'

                rlt += newLine
            })
            return rlt
        },
        [formatDecimal],
    )

    const parseSaleDetail = useCallback(
        ({
            isExtendDetail = false,
            bindOrderLines,
            showPrice = true,
            isShowNotes,
            hideHeader = false,
            isGroupByCategory = false,
            useSecondaryTextStyle = false,
            receiptTemplateType,
        }: ParseSaleDetailProps) => {
            let rlt = showPrice ? '{w:*,5,13}\n' : '{w:*,4}\n' // setup width of each columns

            if (!hideHeader)
                rlt += `|"Name ${
                    !showPrice
                        ? '| "Qty|'
                        : `| "Qty | "Price (${currencySymbol})|`
                }\n`

            const {
                comboRootOrderLines,
                voidedComboRootOrderLines,
                complexRootOrderLines,
                voidedComplexRootOrderLines,
                simpleRootOrderLines,
                voidedSimpleRootOrderLines,
            } = splitOrderItemDetailToLines()

            // Order items on the receipt:
            //   1. combos
            //   2. voided combos
            //   3. items with modifiers/variants
            //   4. voided items with modifiers/variants
            //   5. simple items without modifiers/variants
            //   6. voided simple items without modifiers/variants
            const orderLines = comboRootOrderLines
                .concat(...voidedComboRootOrderLines)
                .concat(...complexRootOrderLines)
                .concat(...voidedComplexRootOrderLines)
                .concat(...simpleRootOrderLines)
                .concat(...voidedSimpleRootOrderLines)

            const renderOrderItemCategoryGroup = (
                orderLines: BindingOrderLine[],
            ) => {
                const splitter = isRemoveBordersAndLines ? '~' : '-'
                const categoryName =
                    orderLines[0].categoryName || 'Uncategorized'
                rlt += `\n${splitter}\n`
                rlt += `${adjustBiggerFont(categoryName, true)}`
                rlt += `\n${splitter}\n`
                orderLines.forEach((line, index) => {
                    renderOrderLine(line, index)
                })
            }

            const renderOrderLine = (
                orderLine: BindingOrderLine,
                index: number,
            ) => {
                const comboItems = bindOrderLines.filter(
                    (l) => l.refOrderLineID === orderLine.orderLineID,
                )

                const lineCount = !isExtendDetail
                    ? orderLines.length
                    : orderLines.length + comboItems.length

                const receiptLine = parseOrderItemLine(
                    orderLine,
                    null,
                    index,
                    lineCount,
                )

                rlt += receiptLine

                if (isExtendDetail && !orderLine.isVoided) {
                    const comboLines = comboItems.map(
                        (c, ci) =>
                            '~ ' +
                            parseOrderItemLine(
                                c,
                                orderLine,
                                index + ci + 1,
                                lineCount,
                            ),
                    )

                    rlt += comboLines.join('')

                    // Add an empty line after combos for readability.
                    if (
                        index < orderLines.length - 1 &&
                        comboLines.length > 0
                    ) {
                        rlt += '\n'
                    }
                }
            }

            const parseOrderItemLine = (
                line: BindingOrderLine,
                parent: BindingOrderLine | null,
                nCurrentLine: number,
                lineCount: number,
            ) => {
                const isBiggerDetail = shouldBiggerDetail(receiptTemplateType)
                const lineIndent = parent ? '~~' : '~'
                const lineReceipts: string[] = []
                const lineItemQuantity = parent
                    ? (line.orderQuantity || 1) * (parent.orderQuantity || 1)
                    : line.orderQuantity || 1

                const isFreeItem = line.isFreeItem

                let itemPrice = isExtendDetail
                    ? line.isSharedItem
                        ? line.subtotal || 0
                        : (isFreeItem ? 0 : line.price || 0) * lineItemQuantity
                    : line.subtotal || 0

                if (
                    isExtendDetail &&
                    line.orderLineComboVariantOptions?.some(
                        (v) => v.priceAdd && v.priceAdd > 0,
                    )
                ) {
                    // If an item has variants with prices, remove those prices from the item's price.
                    itemPrice -= line.orderLineComboVariantOptions.reduce(
                        (a, b) => a + (b.priceAdd || 0) * lineItemQuantity,
                        0,
                    )
                }

                let lineItemName =
                    formatNullableString(
                        `${
                            line.displayName
                                ? `${line.displayName}${
                                      isOpenItemId(line.itemID)
                                          ? ' (Open Item)'
                                          : ''
                                  }`
                                : line.itemName
                        }`,
                        'Unnamed Item',
                    ) + (isFreeItem ? ' (Free)' : '')

                if (showPrice && lineItemQuantity > 1 && itemPrice !== 0) {
                    lineItemName += ` (@${formatDecimal(
                        itemPrice / lineItemQuantity,
                    )})`
                }
                if (line.isSharedItem) {
                    lineItemName += ' (Shared)'
                }
                if (line.isComped && !parent) {
                    lineItemName +=
                        ' (COMP ' +
                        `${
                            line.isCompPercentage
                                ? `${line.compValue}%`
                                : `${currencySymbol}${formatDecimal(
                                      line.compValue,
                                  )}`
                        } Off - ${line.voidCompReason})`
                }
                if (line.isVoided) {
                    lineItemName += ` (VOID - ${line.voidCompReason})`
                }
                lineReceipts.push(
                    `${lineItemName} | ${
                        showPrice
                            ? line.isVoided
                                ? `${lineItemQuantity} | `
                                : `${lineItemQuantity} | ${formatDecimal(
                                      itemPrice,
                                  )}|`
                            : `${lineItemQuantity}|`
                    }`,
                )

                if (isExtendDetail && !line.isVoided) {
                    if (!line.refOrderLineID) {
                        line.variantOptionNames?.forEach((name) => {
                            lineReceipts.push(
                                `${lineIndent} * ${formatNullableString(
                                    name,
                                    '',
                                )} | |`,
                            )
                        })
                    }
                    //case combo item
                    else {
                        line.orderLineComboVariantOptions?.forEach(
                            (variant) => {
                                const variantOption = variant.variantOption

                                let variantQuantity = lineItemQuantity
                                if (
                                    line.comboChoice?.type ===
                                    ComboChoiceType.FixedProduct
                                ) {
                                    variantQuantity =
                                        lineItemQuantity /
                                        (line.comboChoice.quantity || 1)
                                }

                                const lineVariantPriceAddSubTotal =
                                    (variant.priceAdd || 0) * variantQuantity

                                let lineVariantName = formatNullableString(
                                    variantOption?.name,
                                    '',
                                )

                                if (
                                    showPrice &&
                                    lineItemQuantity > 1 &&
                                    lineVariantPriceAddSubTotal !== 0
                                ) {
                                    lineVariantName += ` (@${formatDecimal(
                                        lineVariantPriceAddSubTotal /
                                            lineItemQuantity,
                                    )})`
                                }

                                lineReceipts.push(
                                    `${
                                        useSecondaryTextStyle ? '`' : ''
                                    }${lineIndent} * ${lineVariantName} | ${
                                        useSecondaryTextStyle ? '`' : ''
                                    }${lineItemQuantity}${
                                        showPrice
                                            ? ` | ${formatDecimal(
                                                  lineVariantPriceAddSubTotal,
                                              )}|`
                                            : '|'
                                    }`,
                                )
                            },
                        )
                    }

                    line.orderLineModifiers?.forEach((modifier) => {
                        const modOption = modifier.modifierOption
                        const modQuantity =
                            lineItemQuantity * (modifier.quantity || 1)
                        const modTotalPriceAdd =
                            modQuantity * (modifier.priceAdd || 0)
                        let modName = formatNullableString(
                            modOption?.displayName || modOption?.itemName,
                            '',
                        )
                        if (
                            showPrice &&
                            modQuantity > 1 &&
                            modifier.priceAdd &&
                            modifier.priceAdd !== 0
                        ) {
                            modName += ` (@${formatDecimal(modifier.priceAdd)})`
                        }
                        lineReceipts.push(
                            `${useSecondaryTextStyle ? '`' : ''}${
                                modOption?.type === ModifierOptionType.Add
                                    ? `${lineIndent} +`
                                    : `${lineIndent} -`
                            } ${modName} | ` +
                                (showPrice
                                    ? `${
                                          useSecondaryTextStyle ? '`' : ''
                                      }${modQuantity} | ${formatDecimal(
                                          modTotalPriceAdd,
                                      )}|`
                                    : `${
                                          useSecondaryTextStyle ? '`' : ''
                                      }${modQuantity}|`),
                        )
                    })
                }

                if (
                    receiptTemplateType === ReceiptTemplateType.Kitchen &&
                    line.seatNote
                ) {
                    lineReceipts.push(
                        `Seat: ${displaySeatNote(line.seatNote)} |`,
                    )
                }

                // Add order line note.
                if (isShowNotes && line.note) {
                    lineReceipts.push(
                        `|~~${useSecondaryTextStyle ? '`' : ''}${
                            isBiggerDetail ? '"' : ''
                        }Note: ${line.note}`,
                    )
                }

                if (nCurrentLine < lineCount - 1) {
                    lineReceipts.push(' ')
                }
                // Add an empty line after items with modifiers/variants for readability.
                if (
                    isExtendDetail &&
                    nCurrentLine < lineCount - 1 &&
                    !parent &&
                    ((line.orderLineModifiers?.length || 0) > 0 ||
                        (line.variantOptionNames?.length || 0) > 0 ||
                        (line.orderLineComboVariantOptions?.length || 0) > 0)
                ) {
                    lineReceipts.push(' ')
                } else if (
                    nCurrentLine < lineCount - 1 &&
                    !parent &&
                    !line.refOrderLineID &&
                    (line.isVoided ||
                        (line.isComped &&
                            (!bindOrderLines.some(
                                (l) => l.refOrderLineID === line.orderLineID,
                            ) ||
                                !isExtendDetail)))
                ) {
                    // Add an empty line after items with voids/comps for readability.
                    lineReceipts.push(' ')
                }

                return lineReceipts
                    .map((l) => adjustBiggerFont(l, isBiggerDetail))
                    .join('\n')
            }

            if (isGroupByCategory) {
                // Group order items by category.
                const groupedOrderLines = orderLines.reduce(
                    (acc, orderLine) => {
                        const categoryID = orderLine.categoryID || Guid.empty
                        if (!acc[categoryID]) {
                            acc[categoryID] = []
                        }
                        acc[categoryID].push(orderLine)
                        return acc
                    },
                    {} as Record<string, BindingOrderLine[]>,
                )
                const sortedGroupedOrderLines = Object.keys(groupedOrderLines)
                    .map((categoryID) => ({
                        categoryID,
                        orderLines: groupedOrderLines[categoryID],
                    }))
                    .sort(kitchenCategorySortFunction)

                Object.values(sortedGroupedOrderLines).forEach((grouped) => {
                    renderOrderItemCategoryGroup(grouped.orderLines)
                })
            } else {
                // Sort orderLines by sortIndex even if not grouped by category.
                const sortedOrderLines = orderLines.sort(
                    kitchenCategorySortFunction,
                )

                sortedOrderLines.forEach((orderLine, index) => {
                    renderOrderLine(orderLine, index)
                })
            }

            return rlt

            function splitOrderItemDetailToLines() {
                const comboRootOrderLines = bindOrderLines.filter(
                    (i) =>
                        isNull(i.refOrderLineID) &&
                        bindOrderLines.some(
                            (o) => o.refOrderLineID === i.orderLineID,
                        ) &&
                        !i.isVoided,
                )
                const voidedComboRootOrderLines = bindOrderLines.filter(
                    (i) =>
                        isNull(i.refOrderLineID) &&
                        bindOrderLines.some(
                            (o) => o.refOrderLineID === i.orderLineID,
                        ) &&
                        i.isVoided,
                )
                const complexRootOrderLines = bindOrderLines.filter(
                    (i) =>
                        isNull(i.refOrderLineID) &&
                        !bindOrderLines.some(
                            (o) => o.refOrderLineID === i.orderLineID,
                        ) &&
                        ((i.variantOptionNames &&
                            i.variantOptionNames.length > 0) ||
                            (i.orderLineComboVariantOptions &&
                                i.orderLineComboVariantOptions.length > 0) ||
                            (i.orderLineModifiers &&
                                i.orderLineModifiers.length > 0)) &&
                        !i.isVoided,
                )
                const voidedComplexRootOrderLines = bindOrderLines.filter(
                    (i) =>
                        isNull(i.refOrderLineID) &&
                        !bindOrderLines.some(
                            (o) => o.refOrderLineID === i.orderLineID,
                        ) &&
                        ((i.variantOptionNames &&
                            i.variantOptionNames.length > 0) ||
                            (i.orderLineComboVariantOptions &&
                                i.orderLineComboVariantOptions.length > 0) ||
                            (i.orderLineModifiers &&
                                i.orderLineModifiers.length > 0)) &&
                        i.isVoided,
                )
                const simpleRootOrderLines = bindOrderLines.filter(
                    (i) =>
                        isNull(i.refOrderLineID) &&
                        !bindOrderLines.some(
                            (o) => o.refOrderLineID === i.orderLineID,
                        ) &&
                        (!i.variantOptionNames ||
                            i.variantOptionNames.length === 0) &&
                        (!i.orderLineComboVariantOptions ||
                            i.orderLineComboVariantOptions.length === 0) &&
                        (!i.orderLineModifiers ||
                            i.orderLineModifiers.length === 0) &&
                        !i.isVoided,
                )
                const voidedSimpleRootOrderLines = bindOrderLines.filter(
                    (i) =>
                        isNull(i.refOrderLineID) &&
                        !bindOrderLines.some(
                            (o) => o.refOrderLineID === i.orderLineID,
                        ) &&
                        (!i.variantOptionNames ||
                            i.variantOptionNames.length === 0) &&
                        (!i.orderLineComboVariantOptions ||
                            i.orderLineComboVariantOptions.length === 0) &&
                        (!i.orderLineModifiers ||
                            i.orderLineModifiers.length === 0) &&
                        i.isVoided,
                )
                return {
                    comboRootOrderLines,
                    voidedComboRootOrderLines,
                    complexRootOrderLines,
                    voidedComplexRootOrderLines,
                    simpleRootOrderLines,
                    voidedSimpleRootOrderLines,
                }
            }
        },
        [
            currencySymbol,
            formatNullableString,
            formatDecimal,
            displaySeatNote,
            kitchenCategorySortFunction,
            isRemoveBordersAndLines,
        ],
    )

    const parseSaleDetailBySaleItems = useCallback(
        (
            extend: boolean = false,
            bindSaleItems: BindingSaleItem[],
            showPrice: boolean = true,
            receiptTemplateType: number | undefined,
        ) => {
            const isBiggerDetail = shouldBiggerDetail(receiptTemplateType)
            let rlt = showPrice ? '{w:*,5,13}\n' : '{w:*,4}\n' // setup width of each columns
            rlt += `|"Name ${
                !showPrice ? '| "Qty|' : `| "Qty | "Price (${currencySymbol})|`
            }\n`

            const sumSubtotals = (saleItem: BindingSaleItem) => {
                let price = saleItem.subtotal || 0
                // Recursively add children's subtotals.
                price += bindSaleItems
                    .filter((s) => s.parentSaleItemID === saleItem.saleItemID)
                    .reduce((a, b) => a + sumSubtotals(b), 0)
                return price
            }

            const parseSaleItem = (
                saleItem: BindingSaleItem,
                level: number,
            ) => {
                const lines: string[] = []
                let receiptLine: string = ''

                if (level > 0) {
                    receiptLine += '~'.repeat(level)
                }

                if (saleItem.type === SaleItemType.ProductVariant) {
                    receiptLine += '* '
                } else if (saleItem.type === SaleItemType.Modifier) {
                    receiptLine +=
                        saleItem.isDeduct === null
                            ? '' // Not totally necessary, but just in case, since a modifier should always have a value for isDeduct.
                            : saleItem.isDeduct === true
                            ? '- '
                            : '+ '
                } else if (
                    saleItem.parentSaleItem &&
                    saleItem.parentSaleItem.type === SaleItemType.Combo
                ) {
                    receiptLine += '' // TODO: We might consider adding something before combo items to distinguish them from normal (SaleItemType.Product) items. Currently indentation is used.
                }

                let price = saleItem.subtotal || 0

                if (!extend) {
                    // If not extend, then add all children's subtotals to get full price for this SaleItem.
                    price = sumSubtotals(saleItem)
                }

                let itemName = formatNullableString(saleItem.name, '')
                itemName =
                    (saleItem.isOpenItem
                        ? `${itemName} (Open Item)`
                        : itemName) + (saleItem.isFreeItem ? ' (Free)' : '')
                if (
                    showPrice &&
                    saleItem.quantity &&
                    saleItem.quantity > 1 &&
                    price &&
                    price !== 0
                ) {
                    itemName += ` (@${formatDecimal(
                        price / saleItem.quantity,
                    )})`
                }

                if ((saleItem.quantity || 0) > 0) {
                    receiptLine += `${itemName} | ${
                        showPrice
                            ? saleItem.state === SaleItemState.Void
                                ? `${saleItem.quantity} | `
                                : `${saleItem.quantity} | ${formatDecimal(
                                      price,
                                  )}|`
                            : `${saleItem.quantity}|`
                    }`
                } else {
                    receiptLine += `${itemName} | |`
                }
                lines.push(receiptLine)
                //child lines
                if (extend && saleItem.state !== SaleItemState.Void) {
                    const childVariantSaleItems = bindSaleItems.filter(
                        (i) =>
                            i.parentSaleItemID === saleItem.saleItemID &&
                            i.type === SaleItemType.ProductVariant,
                    )
                    childVariantSaleItems.forEach((childSaleItem) => {
                        lines.push(parseSaleItem(childSaleItem, level + 1))
                    })

                    const childAddModifierSaleItems = bindSaleItems.filter(
                        (i) =>
                            i.parentSaleItemID === saleItem.saleItemID &&
                            i.type === SaleItemType.Modifier &&
                            i.isDeduct === false,
                    )
                    childAddModifierSaleItems.forEach((childSaleItem) => {
                        lines.push(parseSaleItem(childSaleItem, level + 1))
                    })

                    const childDeductModifierSaleItems = bindSaleItems.filter(
                        (i) =>
                            i.parentSaleItemID === saleItem.saleItemID &&
                            i.type === SaleItemType.Modifier &&
                            i.isDeduct === true,
                    )
                    childDeductModifierSaleItems.forEach((childSaleItem) => {
                        lines.push(parseSaleItem(childSaleItem, level + 1))
                    })

                    const childProductOrComboSaleItems = bindSaleItems.filter(
                        (i) =>
                            i.parentSaleItemID === saleItem.saleItemID &&
                            (i.type === SaleItemType.Product ||
                                i.type === SaleItemType.Combo),
                    )
                    childProductOrComboSaleItems.forEach((childSaleItem) => {
                        lines.push(parseSaleItem(childSaleItem, level + 1))
                    })
                }
                if (lines.length > 1)
                    return lines
                        .map((a) => adjustBiggerFont(a, isBiggerDetail))
                        .join('\n')
                return receiptLine
            }

            const comboRootSaleItems = bindSaleItems.filter(
                (i) =>
                    i.type === SaleItemType.Combo &&
                    i.state !== SaleItemState.Void,
            )
            const voidedComboRootSaleItems = bindSaleItems.filter(
                (i) =>
                    i.type === SaleItemType.Combo &&
                    i.state === SaleItemState.Void,
            )
            const complexRootSaleItems = bindSaleItems.filter(
                (i) =>
                    i.type !== SaleItemType.Combo &&
                    !i.parentSaleItem &&
                    bindSaleItems.some(
                        (j) => j.parentSaleItemID === i.saleItemID,
                    ) &&
                    i.state !== SaleItemState.Void,
            )
            const voidedComplexRootSaleItems = bindSaleItems.filter(
                (i) =>
                    i.type !== SaleItemType.Combo &&
                    !i.parentSaleItem &&
                    bindSaleItems.some(
                        (j) => j.parentSaleItemID === i.saleItemID,
                    ) &&
                    i.state === SaleItemState.Void,
            )
            const simpleRootSaleItems = bindSaleItems.filter(
                (i) =>
                    i.type !== SaleItemType.Combo &&
                    !i.parentSaleItem &&
                    !bindSaleItems.some(
                        (j) => j.parentSaleItemID === i.saleItemID,
                    ) &&
                    i.state !== SaleItemState.Void,
            )
            const voidedSimpleRootSaleItems = bindSaleItems.filter(
                (i) =>
                    i.type !== SaleItemType.Combo &&
                    !i.parentSaleItem &&
                    !bindSaleItems.some(
                        (j) => j.parentSaleItemID === i.saleItemID,
                    ) &&
                    i.state === SaleItemState.Void,
            )
            // Order items on the receipt:
            //   1. combos
            //   2. voided combos
            //   3. items with modifiers/variants
            //   4. voided items with modifiers/variants
            //   5. simple items without modifiers/variants
            //   6. voided simple items without modifiers/variants
            const rootSaleItems = comboRootSaleItems
                .concat(...voidedComboRootSaleItems)
                .concat(...complexRootSaleItems)
                .concat(...voidedComplexRootSaleItems)
                .concat(...simpleRootSaleItems)
                .concat(...voidedSimpleRootSaleItems)
            rootSaleItems.forEach((rootSaleItem, index) => {
                if (index > 0) {
                    rlt += '\n'
                }

                const line = parseSaleItem(rootSaleItem, 0) // Start at level 0 since this is a root SaleItem.
                rlt += line

                // Add an empty line to help with readability.
                // Applies to combos and items with children (modifiers/variants).
                if (
                    extend &&
                    index !== rootSaleItems.length - 1 &&
                    (rootSaleItem.type === SaleItemType.Combo ||
                        bindSaleItems.some(
                            (s) =>
                                s.parentSaleItemID === rootSaleItem.saleItemID,
                        ))
                ) {
                    rlt += '\n'
                } else if (
                    index !== rootSaleItems.length - 1 &&
                    (rootSaleItem.state === SaleItemState.Void ||
                        rootSaleItem.state === SaleItemState.Comp)
                ) {
                    rlt += '\n'
                }
            })
            return rlt
        },
        [currencySymbol, formatNullableString, formatDecimal],
    )

    const parseOrderComp = useCallback(
        (comp: BindingCompModel) => {
            let line = '\n{width: *}'
            line += isRemoveBordersAndLines ? '\n~\n' : '\n --- \n'
            line += `COMP ${
                comp.isPercentage
                    ? `${comp.value}%`
                    : `${currencySymbol}${formatDecimal(comp.value)}`
            } Off - ${comp.reason}`
            line += '\n{width: * *}'
            return line
        },
        [formatDecimal, currencySymbol, isRemoveBordersAndLines],
    )

    const parseRefundLineItems = useCallback(
        (
            data: ReceiptBindingDataContract,
            newMarkup: string,
            receiptTemplateType: number | undefined,
        ) => {
            const orderLines = data.refundPartialOrder?.orderLines ?? []
            if (orderLines.length === 0) return
            const rtl: string[] = [`-\n|Refunded(partial) :`]

            const isRefundLineItemExtend = newMarkup.includes(
                lineOptionMap.SALE_LINE_ITEM_SPEC,
            )
            const isRefundLineItemPrice = newMarkup.includes(
                lineOptionMap.SALE_LINE_ITEM_PRICE,
            )
            const isGroupByCategory = newMarkup.includes(
                lineOptionMap.SALE_LINE_ITEM_GROUP_CATEGORY,
            )
            const isShowNotes = newMarkup.includes(
                lineOptionMap.SALE_LINE_ITEM_NOTE,
            )

            rtl.push(
                parseSaleDetail({
                    isExtendDetail: isRefundLineItemExtend,
                    bindOrderLines: orderLines,
                    showPrice: isRefundLineItemPrice,
                    isShowNotes,
                    hideHeader: true,
                    isGroupByCategory,
                    useSecondaryTextStyle: false,
                    receiptTemplateType,
                }),
            )
            rtl.push('{width: * *}')
            const returnTax =
                (data?.refundPartialOrder?.taxExclusiveRefundAmount ?? 0) +
                (data?.refundPartialOrder?.taxInclusiveRefundAmount ?? 0)
            if (returnTax > 0)
                rtl.push(`|Refund Tax: | ${decimalFormat(returnTax)}|`)
            if ((data?.refundPartialOrder?.serviceChargeRefundAmount ?? 0) > 0)
                rtl.push(
                    `|Refund Service Charge: | ${decimalFormat(
                        data?.refundPartialOrder?.serviceChargeRefundAmount ??
                            0,
                    )}|`,
                )
            if ((data?.refundPartialOrder?.tipRefundAmount ?? 0) > 0)
                rtl.push(
                    `|Refund Tip: | ${decimalFormat(
                        data?.refundPartialOrder?.tipRefundAmount ?? 0,
                    )}|`,
                )
            return rtl.join('\n')
        },
        [decimalFormat, parseSaleDetail],
    )

    const parseSaleLineItems = useCallback(
        (
            newMarkup: string,
            data: ReceiptBindingDataContract,
            receiptTemplateType: number | undefined,
        ) => {
            const isSaleLineItemExtend = newMarkup.includes(
                lineOptionMap.SALE_LINE_ITEM_SPEC,
            )
            const isSaleLineItemPrice = newMarkup.includes(
                lineOptionMap.SALE_LINE_ITEM_PRICE,
            )
            const isGroupByCategory = newMarkup.includes(
                lineOptionMap.SALE_LINE_ITEM_GROUP_CATEGORY,
            )
            const isShowNotes = newMarkup.includes(
                lineOptionMap.SALE_LINE_ITEM_NOTE,
            )
            const useSecondaryTextStyle = newMarkup.includes(
                lineOptionMap.USE_SECONDARY_TEXT_STYLE,
            )
            let saleDetail = ''
            if (data.saleItems) {
                saleDetail = parseSaleDetailBySaleItems(
                    isSaleLineItemExtend,
                    data.saleItems,
                    isSaleLineItemPrice,
                    receiptTemplateType,
                )
            } else {
                // Needed for Bill Preview feature, since SaleItem records are not yet created at this point.
                saleDetail = parseSaleDetail({
                    isExtendDetail: isSaleLineItemExtend,
                    bindOrderLines: data.order?.orderLines || [],
                    showPrice: isSaleLineItemPrice,
                    isShowNotes,
                    hideHeader: false,
                    isGroupByCategory,
                    useSecondaryTextStyle,
                    receiptTemplateType,
                })
            }
            if (data.comp) {
                saleDetail += parseOrderComp(data.comp)
            }
            return saleDetail
        },
        [parseOrderComp, parseSaleDetail, parseSaleDetailBySaleItems],
    )
    const paymentDetail = useCallback(
        (
            payment: SalePaymentWithChange,
            totalPayment: number,
            setting: {
                isMultiLine: boolean
                isDisplayChange: boolean
                isDisplayAmount: boolean
                isDisplayGiftCardBalance: boolean
            },
        ): string => {
            let {
                paymentTypeID,
                paymentAccountName,
                paymentTypeName,
                maskCardNumber,
                transactionID,
                actionCompleteDateTime,
                changeAmount,
                amount,
                customPaymentTypeID,
                remainingGiftCardBalance,
                voidedDateTime,
                voidedTransactionID,
            } = payment

            const accountName = formatNullableString(paymentAccountName, '')
            const cardNumber = formatNullableString(maskCardNumber, '')
            const paymentTypeLabel = mapPaymentTypeName(
                paymentTypeID!,
                customPaymentTypeID ? paymentTypeName! : '',
            )

            const rtl: string[] = []
            if (!isEmpty(cardNumber) || transactionID) {
                if (!setting.isMultiLine) rtl.push(` ${paymentTypeLabel}|`)
                rtl.push(
                    praseCreditCardPayment(
                        cardNumber,
                        transactionID,
                        actionCompleteDateTime,
                        paymentTypeLabel,
                        setting.isMultiLine,
                        setting.isDisplayAmount ? totalPayment : undefined,
                    ),
                )
            } else {
                if (!setting.isMultiLine) {
                    const paymentTypeWithAccount = `${paymentTypeLabel} ${
                        paymentAccountName ?? ''
                    }`.trim()
                    //maximum column lenght for payment detail is around 22
                    if (paymentTypeWithAccount.length > 22) {
                        rtl.push(` ${paymentTypeName}|`)
                        rtl.push('{width: *}')
                        rtl.push(`|~${paymentAccountName}`)
                    } else rtl.push(` ${paymentTypeWithAccount}|`)
                } else {
                    rtl.push(
                        `|~${paymentTypeLabel}${
                            accountName ? ` ${accountName}` : ''
                        } | ${
                            setting.isDisplayAmount
                                ? formatDecimal(totalPayment)
                                : ''
                        }|`,
                    )
                    if (
                        setting.isDisplayGiftCardBalance &&
                        paymentTypeID === PAYMENT_TYPE_ID.GIFTCARD &&
                        remainingGiftCardBalance
                    )
                        rtl.push(
                            `|~~~Remaining Balance: ${formatDecimal(
                                remainingGiftCardBalance,
                            )}`,
                        )

                    if (
                        setting.isDisplayChange &&
                        paymentTypeID === PAYMENT_TYPE_ID.CASH
                    )
                        rtl.push(cashPaymentDetail(amount, changeAmount))
                }
            }
            if (voidedDateTime) {
                rtl.push('{width: *}')
                if (voidedTransactionID)
                    rtl.push(
                        `|${
                            setting.isMultiLine ? '~~~' : '~'
                        }Void Ref. No.: ${voidedTransactionID}`,
                    )
                if (voidedDateTime)
                    rtl.push(
                        `|${
                            setting.isMultiLine ? '~~~' : '~'
                        }Voided Date: ${shortDateFormat(
                            dayjs(voidedDateTime),
                        )} ~Time: ${time(dayjs(voidedDateTime), true, true)}`,
                    )
            }
            return rtl.join('\n')

            function cashPaymentDetail(
                amount: number | undefined,
                changeAmount: number | undefined,
            ) {
                const rtl: string[] = []
                rtl.push(`|~~~Paid Amount: ${formatDecimal(amount)}`)
                rtl.push(`|~~~Change: ${formatDecimal(changeAmount ?? 0)}`)
                return rtl.join('\n')
            }

            function praseCreditCardPayment(
                maskCardNumber: string | null | undefined,
                transactionID: string | null | undefined,
                actionCompleteDateTime: string | null | undefined,
                paymentTypeLabel: string,
                showDetail: boolean,
                totalPayment?: number,
            ) {
                if (!transactionID)
                    return `|~${paymentTypeLabel} ${maskCardNumber}${
                        totalPayment ? ` | ${formatDecimal(totalPayment)}|` : ''
                    }`

                const rtl: string[] = []
                if (showDetail) {
                    rtl.push('{width: 30 *}')
                    rtl.push(
                        `|~${paymentTypeLabel} ${maskCardNumber} | ${
                            totalPayment ? formatDecimal(totalPayment) : ''
                        }|`,
                    )
                    rtl.push('{width:*}')
                    rtl.push(`|~~~Ref. No.: ${transactionID}`)
                    rtl.push(
                        `|~~~Date: ${shortDateFormat(
                            dayjs(actionCompleteDateTime),
                        )}`,
                    )
                    rtl.push(
                        `|~~~Time: ${time(
                            dayjs(actionCompleteDateTime),
                            true,
                            true,
                        )}`,
                    )
                } else {
                    rtl.push('{width: *}')
                    rtl.push(
                        `${
                            maskCardNumber ? `|~${maskCardNumber} ` : '|'
                        }~Ref. No.: ${transactionID}`,
                    )
                    rtl.push(
                        `|~Date: ${shortDateFormat(
                            dayjs(actionCompleteDateTime),
                        )} ~Time: ${time(
                            dayjs(actionCompleteDateTime),
                            true,
                            true,
                        )}`,
                    )
                }
                rtl.push('{width: * *}')
                return rtl.join('\n')
            }
        },
        [formatDecimal, formatNullableString, shortDateFormat, time],
    )
    const parseRefundedMethod = useCallback(
        (payments: SalePaymentInfoContract[]) => {
            if (payments.length === 0) return ''

            const refundedPayments = mergeSamePaymentDetails(
                payments.filter((c) => c.status === PaymentStatus.Refunded),
                false,
            )

            if (refundedPayments.length === 1) {
                const rtl: string[] = []
                const payment = paymentDetail(
                    refundedPayments[0],
                    refundedPayments[0]?.amount ?? 0,
                    {
                        isMultiLine: false,
                        isDisplayChange: false,
                        isDisplayAmount: true,
                        isDisplayGiftCardBalance: false,
                    },
                )
                rtl.push(paymentMethodHeader('Refund Method', payment))
                return rtl.join('\n')
            }
            const rtl: string[] = [paymentMethodHeader('Refund Method')]
            refundedPayments.forEach((payment) => {
                rtl.push(
                    paymentDetail(payment, payment.amount ?? 0, {
                        isMultiLine: true,
                        isDisplayChange: false,
                        isDisplayAmount: true,
                        isDisplayGiftCardBalance: false,
                    }),
                )
            })

            return rtl.join('\n')
        },
        [paymentDetail],
    )
    const parseVoidedPaymentMethod = useCallback(
        (markup: string, payments: SalePaymentInfoContract[]) => {
            const isDisplayChange = markup.includes('+change')
            const isDisplayTotal = markup.includes('+amount')
            if (payments.length === 0) return ''

            const voidedPayment = mergeSamePaymentDetails(
                payments.filter(
                    (c) =>
                        c.status === PaymentStatus.Cancelled ||
                        c.status === PaymentStatus.Voided,
                ),
                isDisplayChange,
            )

            const rtl: string[] = []

            if (voidedPayment.length > 0) {
                rtl.push('|Voided Payments: ')
                voidedPayment.forEach((p) => {
                    const totalPayment = (p.amount ?? 0) - (p.changeAmount ?? 0)
                    rtl.push(
                        paymentDetail(p, totalPayment, {
                            isMultiLine: true,
                            isDisplayChange: isDisplayChange,
                            isDisplayAmount: isDisplayTotal,
                            isDisplayGiftCardBalance: false,
                        }),
                    )
                })
            }
            return rtl.join('\n')
        },
        [paymentDetail],
    )
    const parsePaymentMethod = useCallback(
        (
            newMarkup: string,
            payments: SalePaymentInfoContract[],
            isOverPaid: boolean,
        ) => {
            const isDisplayChange = newMarkup.includes('+change')
            const isDisplayTotal = newMarkup.includes('+amount')
            const isDisplayGiftCardBalance = newMarkup.includes(
                '+giftcardBalance',
            )
            if (payments.length === 0) return ''

            const displayPayments = mergeSamePaymentDetails(
                payments ?? [],
                isDisplayChange,
            )

            const receivedPayments = displayPayments.filter(
                (c) =>
                    c.status !== PaymentStatus.Cancelled &&
                    c.status !== PaymentStatus.Voided,
            )

            if (receivedPayments.length === 1) {
                const rtl: string[] = []
                const payment = paymentDetail(
                    receivedPayments[0],
                    receivedPayments[0]?.amount ?? 0,
                    {
                        isMultiLine: false,
                        isDisplayChange,
                        isDisplayAmount: isDisplayTotal,
                        isDisplayGiftCardBalance,
                    },
                )
                rtl.push(paymentMethodHeader('Payment Method', payment))

                return rtl.join('\n')
            }

            if (receivedPayments.length === 0) {
                // No Payments because of Credit Sale (Negative Balance Due Total) -> Cash only
                const rtl: string[] = [
                    paymentMethodHeader('Payment Method', 'Cash'),
                ]

                return rtl.join('\n')
            }

            // Multiple Payments
            const rtl: string[] = [
                paymentMethodHeader(
                    `Payment Method ${
                        isOverPaid ? '(Overpaid)' : '(Split Payments)'
                    }`,
                ),
            ]

            receivedPayments.forEach((payment) => {
                const totalPayment =
                    (payment.amount ?? 0) - (payment.changeAmount ?? 0)
                rtl.push(
                    paymentDetail(payment, totalPayment, {
                        isMultiLine: true,
                        isDisplayChange,
                        isDisplayAmount: isDisplayTotal,
                        isDisplayGiftCardBalance,
                    }),
                )
            })
            return rtl.join('\n')
        },
        [paymentDetail],
    )

    const parseFutureDiscountDetail = useCallback(
        (discounts: DiscountRedemptionDetailContract[]) => {
            let rtl = ''
            discounts.forEach((d) => {
                rtl += isRemoveBordersAndLines ? '~\n' : '-\n'
                rtl += `${formatNullableString(d.discountName, 'Discount ')}\n`
                if (d.redeemCode) {
                    rtl += `Redeem Code:${d.redeemCode}\n\n`
                    rtl += `{code:${d.redeemCode}; option:qrcode,3,L}\n\n`
                }
                if (d.discountAvailability) {
                    const {
                        availableDays,
                        startDate,
                        startTime,
                        endDate,
                        endTime,
                    } = d.discountAvailability
                    if (availableDays) {
                        rtl += availableDays
                            ?.split(';')
                            .slice(1)
                            .filter((f) => !isEmpty(f))
                            .sort()
                            .map(
                                (m: string) =>
                                    dayjs.weekdaysShort()[parseInt(m)],
                            )
                            .join(', ')
                        rtl += ' '
                    }
                    const validTime = [startTime!, endTime!].filter((b) =>
                        Boolean(b),
                    )
                    if (validTime.length > 0) {
                        rtl += validTime
                            .map((t) => {
                                return time(
                                    `${dayjs().format(`YYYY-MM-DDT${t}`)}`,
                                )
                            })
                            .join(' - ')
                    }
                    if (availableDays || startTime || endTime) rtl += '\n'
                    const validDate = [startDate!, endDate!].filter(
                        (b) => b !== undefined,
                    )

                    if (validDate.length > 0) {
                        rtl +=
                            'Valid Thru: ' +
                            validDate
                                .map((date) => shortDateFormat(date))
                                .join(' - ') +
                            '\n'
                    }
                }
            })
            return rtl
        },
        [formatNullableString, shortDateFormat, time, isRemoveBordersAndLines],
    )

    const parseCustomerDetail = useCallback(
        (data: BindingOrderModel) => {
            let result = '{border:space; width: 13 *}'
            result += isRemoveBordersAndLines ? '\n~\n' : '\n-\n'
            result += `|Name: | ${formatNullableString(
                data.deliveryCustomerName,
            )}|\n`
            result += `|Email: | ${formatNullableString(
                data.deliveryEmailContract,
            )}|\n`
            result += `|Phone Number: | ${formatNullableString(
                data.deliveryPhoneContract,
                '-',
            )}|\n`
            result += `|Address: | ${formatNullableString(
                data.deliveryAddress?.replaceAll('\n', ' '),
            )}|\n`
            result += `|GPS Location: | ${data.deliverLongitude || '0'}, ${
                data.deliverLatitude || '0'
            }|\n`
            result += `{border:space; width: *} \n|Delivery Note: \n ~ ${formatNullableString(
                data?.deliveryNote?.replaceAll('\n', '; '),
                '-',
            )} |\n`
            return result
        },
        [formatNullableString, isRemoveBordersAndLines],
    )

    const parseOWTemplate = useCallback(
        ({ markup, data, receiptTemplateType }: ITemplateData) => {
            const isBiggerDetail = shouldBiggerDetail(receiptTemplateType)

            //* {template:ow;label:foo;value:bar} => foo bar
            //* {template:ow;value:bar} => bar
            if (markup.includes(ORDERWIRE_MARKUP.template)) {
                const newMarkup = markup.replace(/([{}])/g, '')
                const perAttr = newMarkup.split(';').map((i) => i.trim())
                const templateAttr = perAttr.find((i) =>
                    i.includes(ORDERWIRE_MARKUP.template),
                )
                const labelAttr = perAttr.find((i) =>
                    i.includes(ORDERWIRE_MARKUP.label),
                )
                const valueAttr = perAttr.find((i) =>
                    i.includes(ORDERWIRE_MARKUP.value),
                )
                const sectionAttr = perAttr.find((i) =>
                    i.includes(ORDERWIRE_MARKUP.section),
                )
                //reset column setting from other parse receipt template
                let rlt = '{width:auto} \n'

                if (templateAttr?.split(':')[1] !== 'ow') return rlt

                const isCol = newMarkup.includes('col')
                const isImage = newMarkup.includes('image')
                const isQRCode = newMarkup.includes('qr-code')
                const isBarcode = newMarkup.includes('barcode')
                const isBold = newMarkup.includes('bold')

                if (sectionAttr) {
                    const section = getValue(
                        sectionAttr,
                        ORDERWIRE_MARKUP.section,
                    )
                    if (
                        section.includes('DELIVERY') &&
                        data.order?.orderFulfillmentTypeID !==
                            OrderFulfilmentType.DELIVERY
                    )
                        return ''
                }

                if (newMarkup.includes('SALE_LINE_ITEMS')) {
                    return parseSaleLineItems(
                        newMarkup,
                        data,
                        receiptTemplateType,
                    )
                }
                if (newMarkup.includes('REFUNDED_LINE_ITEMS')) {
                    if (!data.isRefundedPartial) return
                    return parseRefundLineItems(
                        data,
                        newMarkup,
                        receiptTemplateType,
                    )
                }
                if (newMarkup.includes('PAYMENT_METHOD')) {
                    return parsePaymentMethod(
                        newMarkup,
                        data.payments?.filter((a) =>
                            [
                                ...PAYMENT_RECEIVE_STATUS,
                                PaymentStatus.Returned,
                            ].includes(a.status!),
                        ) ?? [],
                        !!data.isOverPaidSale,
                    )
                }
                if (newMarkup.includes('VOID_PAYMENTS')) {
                    return parseVoidedPaymentMethod(
                        newMarkup,
                        data.payments?.filter((a) =>
                            [
                                PaymentStatus.Cancelled,
                                PaymentStatus.Voided,
                            ].includes(a.status!),
                        ) ?? [],
                    )
                }
                if (newMarkup.includes('REFUND_METHOD')) {
                    return parseRefundedMethod(data.payments ?? [])
                }
                if (newMarkup.includes('FUTURE_DISCOUNT')) {
                    return parseFutureDiscountDetail(
                        data.discountRedemptionDetails ?? [],
                    )
                }
                if (newMarkup.includes('CUSTOMER_DETAIL')) {
                    return parseCustomerDetail(data.order!)
                }
                if (isImage) {
                    if (companyLogoExtractedType) {
                        return `\n{image:${companyLogoExtractedType}}\n`
                    } else return ''
                }
                if (newMarkup.includes('TAX')) return parseTax(data)

                if (labelAttr) {
                    const label = getValue(labelAttr, ORDERWIRE_MARKUP.label)
                    if (valueAttr) {
                        if (label.length > 20) {
                            rlt += '{width:25 *}\n' // extend column width for long label
                        }
                        if (isBiggerDetail && label === 'Table No:')
                            rlt += '^^^'
                    }
                    rlt += `${label} `
                }

                if (isCol) rlt += ' | '

                if (valueAttr) {
                    const value = getValue(valueAttr)
                    if (value === 'REFUND_LABEL') return '"-REFUND-'
                    if (value === 'SALE_SUMMARY')
                        return isRemoveBordersAndLines ? '~' : '-'
                    if (value === 'GIFT_CARD_REMAINING_BALANCE') {
                        // Skip showing gift card remaining balance line if the receipt not contain any giftcard payment
                        const giftCardPayment = data.payments?.find(
                            (p) => p.paymentTypeID === PAYMENT_TYPE_ID.GIFTCARD,
                        )
                        if (!giftCardPayment?.remainingGiftCardBalance)
                            return ''

                        return (rlt += formatDecimal(
                            giftCardPayment.remainingGiftCardBalance,
                        ))
                    }
                    if (isQRCode)
                        return `\n${rlt}\n\n{code:${value};option:qrcode,3,M}\n`
                    if (isBarcode) {
                        const barcodeValue = getBindingData(value, data)
                        return `{code:${barcodeValue};option:code128,48}\n`
                    }
                    if (isBold) rlt += '"'

                    rlt += getBindingData(value, data)
                }
                return rlt
            }
            return markup
        },
        [
            parseTax,
            parseSaleLineItems,
            parseRefundLineItems,
            parsePaymentMethod,
            parseVoidedPaymentMethod,
            parseRefundedMethod,
            parseFutureDiscountDetail,
            parseCustomerDetail,
            companyLogoExtractedType,
            getBindingData,
            formatDecimal,
            isRemoveBordersAndLines,
        ],
    )

    const parseTemplate = useCallback(
        ({
            markup,
            data,
            receiptTemplateType,
            isDuplicateReceipt,
        }: ITemplateData) => {
            if (isRemoveBordersAndLines) {
                markup = markup
                    .replaceAll('{border:line;}', '')
                    .replaceAll('{border:space;}', '~')
            }

            let markupPerLine = markup
                .split(splitter)
                .filter((i) => !isEmpty(i))

            applySaleItemOption()
            applySaleRefundItemOption()
            applyPaymentMethodOption()
            applyDiscountDisplayOption()

            if (isNil(data.order?.tableName))
                markupPerLine = removeFieldFromTemplate(markupPerLine, [
                    'TABLE_NUMBER',
                ])

            if (isNil(data.seatNumber))
                markupPerLine = removeFieldFromTemplate(markupPerLine, [
                    'SEAT_NUMBER',
                ])

            if (isNil(data.order?.guestCount) || data.order?.guestCount === 0)
                markupPerLine = removeFieldFromTemplate(markupPerLine, [
                    'SEAT_COUNT',
                ])

            if (!isDuplicateReceipt)
                markupPerLine = removeFieldFromTemplate(markupPerLine, [
                    '**** DUPLICATE RECEIPT ****',
                ])

            if (
                roundNumber(data.balanceDueAmount ?? 0, 2) ===
                roundNumber(
                    (data.totalDueAmount ?? 0) + (data.tipAmount ?? 0),
                    2,
                )
            ) {
                markupPerLine = removeFieldFromTemplate(markupPerLine, [
                    'Before Rounding',
                ])
            }

            if ((data.discountAmount ?? 0) === 0)
                markupPerLine = removeFieldFromTemplate(markupPerLine, [
                    'DISCOUNTS',
                ])

            if ((data.changeAmount ?? 0) === 0)
                markupPerLine = removeFieldFromTemplate(markupPerLine, [
                    'PAYMENT_CHANGE',
                ])

            if ((data.tipAmount ?? 0) === 0)
                markupPerLine = removeFieldFromTemplate(markupPerLine, [
                    'TIP_AMOUNT',
                ])

            if ((data.serviceChargeAmount ?? 0) === 0)
                markupPerLine = removeFieldFromTemplate(markupPerLine, [
                    'SERVICE_CHARGES',
                ])
            if ((data.pointRedeemDiscount ?? 0) === 0)
                markupPerLine = removeFieldFromTemplate(markupPerLine, [
                    'POINT_REDEEM_DISCOUNT',
                ])
            if (
                data.order?.orderFulfillmentTypeID !==
                OrderFulfilmentType.DELIVERY
            ) {
                markupPerLine = removeFieldFromTemplate(markupPerLine, [
                    'DELIVERY',
                ])
            }

            const res = markupPerLine.map((i) => {
                return parseOWTemplate({
                    markup: i,
                    data,
                    receiptTemplateType,
                })
            })

            // Add order note.
            if (data.order?.note) {
                res.push('\n')
                res.push(`"Note: ${data.order.note}`)
            }

            // Remove empty lines ('') and double-spaced lines ('~\n~\n')
            const rlt = res
                .filter((l) => l !== '')
                .join('\n')
                .replaceAll('~\n~\n', '~\n')

            return `${rlt}\n\n`
            function applyPaymentMethodOption() {
                const hasPaymentMethod = markupPerLine.some((i) =>
                    i.includes('PAYMENT_METHOD'),
                )
                //single payment will display each line separate, So not remove the field
                const hasMultiplePayments =
                    (data.payments?.filter((a) =>
                        PAYMENT_RECEIVE_STATUS.includes(a.status!),
                    )?.length ?? 0) > 1

                if (markupPerLine.some((i) => i.includes('PAYMENT_CHANGE'))) {
                    markupPerLine = markupPerLine.map((i) => {
                        if (
                            i.includes('PAYMENT_METHOD') ||
                            i.includes('VOID_PAYMENTS')
                        )
                            return `${i}${paymentOptionMap.PAYMENT_CHANGE}`
                        return i
                    })
                    if (hasPaymentMethod && hasMultiplePayments)
                        markupPerLine = removeFieldFromTemplate(markupPerLine, [
                            'PAYMENT_CHANGE',
                        ])
                }
                if (markupPerLine.some((i) => i.includes('PAYMENT_AMOUNT'))) {
                    markupPerLine = markupPerLine.map((i) => {
                        if (
                            i.includes('PAYMENT_METHOD') ||
                            i.includes('VOID_PAYMENTS')
                        )
                            return `${i}${paymentOptionMap.PAYMENT_AMOUNT}`
                        return i
                    })
                    if (hasPaymentMethod && hasMultiplePayments)
                        markupPerLine = removeFieldFromTemplate(markupPerLine, [
                            'PAYMENT_AMOUNT',
                        ])
                }
                if (
                    markupPerLine.some((i) =>
                        i.includes('GIFT_CARD_REMAINING_BALANCE'),
                    )
                ) {
                    markupPerLine = markupPerLine.map((i) => {
                        if (i.includes('PAYMENT_METHOD'))
                            return `${i}${paymentOptionMap.GIFT_CARD_REMAINING_BALANCE}`

                        return i
                    })
                    if (hasPaymentMethod && hasMultiplePayments)
                        markupPerLine = removeFieldFromTemplate(markupPerLine, [
                            'GIFT_CARD_REMAINING_BALANCE',
                        ])
                }
            }
            function applyDiscountDisplayOption() {
                markupPerLine = addDiscountOption(
                    markupPerLine,
                    'DISCOUNTS_DETAIL',
                )
            }

            function applySaleRefundItemOption() {
                markupPerLine = addRefundLineOption(
                    markupPerLine,
                    'REFUNDED_LINE_ITEM_SPEC',
                )
                markupPerLine = addRefundLineOption(
                    markupPerLine,
                    'REFUNDED_LINE_ITEM_PRICE',
                )
            }

            function applySaleItemOption() {
                markupPerLine = addSaleLineOption(
                    markupPerLine,
                    'SALE_LINE_ITEM_SPEC',
                )

                markupPerLine = addSaleLineOption(
                    markupPerLine,
                    'SALE_LINE_ITEM_PRICE',
                )
                markupPerLine = addSaleLineOption(
                    markupPerLine,
                    'SALE_LINE_ITEM_NOTE',
                )
                markupPerLine = addSaleLineOption(
                    markupPerLine,
                    'USE_SECONDARY_TEXT_STYLE',
                )
                // Transform SALE_LINE_ITEMS line from markup to SALE_LINE_ITEMS+category if
                // SALE_LINE_ITEM_GROUP_CATEGORY line exists in the markup.
                // and remove SALE_LINE_ITEM_GROUP_CATEGORY line from the markup.
                markupPerLine = addSaleLineOption(
                    markupPerLine,
                    'SALE_LINE_ITEM_GROUP_CATEGORY',
                )
            }
        },
        [isRemoveBordersAndLines, parseOWTemplate],
    )

    const parsePrintingCommand = useCallback(
        async (
            templateData: ITemplateData,
            overridePrinter?: PrinterContract,
        ) => {
            const receipt = parseTemplate(templateData)
            const command = await transformReceiptCommand(
                receipt,
                overridePrinter,
            )
            return command
        },
        [parseTemplate, transformReceiptCommand],
    )

    const parseReceiptTemplateImage = useCallback(
        (templateData: ITemplateData) => {
            setParsing(true)
            const receipt = parseTemplate(templateData)
            const image = transformReceiptSvgBase64(receipt)
            setPreview(image)
            setParsing(false)
            return image
        },
        [parseTemplate, transformReceiptSvgBase64],
    )

    const printPreview = useCallback(
        async (
            templateData: ITemplateData,
            { isAndroid = false, tableId = '' }: PrintPreviewOption,
        ) => {
            setPrinting(true)
            try {
                if (isAndroid || enableAndroidPrinting) {
                    const printCommand = await parsePrintingCommand(
                        templateData,
                    )
                    sendPrintCommandToAndroidPrintingApp(printCommand)
                } else if (!enableOnScreenPrinter) {
                    const printCommand = await parsePrintingCommand(
                        templateData,
                    )
                    await hardwareAgent.print(printCommand, 'preview')
                } else {
                    parseReceiptTemplateImage(templateData)
                }

                if (!isEmpty(tableId)) {
                    await tableService.onBilledTable(tableId)
                }
            } finally {
                // Even print failed, Printing state must be stopped
                setPrinting(false)
            }
        },
        [
            hardwareAgent,
            enableOnScreenPrinter,
            enableAndroidPrinting,
            parseReceiptTemplateImage,
            parsePrintingCommand,
        ],
    )

    const clearPreview = useCallback(() => {
        setPreview('')
    }, [setPreview])

    return {
        parsing,
        printing,
        clearPreview,
        previewSvg,
        printPreview,
        parsePrintingCommand,
        parseReceiptTemplateImage,
        enableOnScreenPrinter,
    }
}

export default useReceiptTemplate

const lineOptionMap = {
    SALE_LINE_ITEM_SPEC: '+extend',
    SALE_LINE_ITEM_PRICE: '+price',
    SALE_LINE_ITEM_NOTE: '+note',
    USE_SECONDARY_TEXT_STYLE: '+secondStyle',
    SALE_LINE_ITEM_GROUP_CATEGORY: '+category',
}
function addSaleLineOption(
    markupPerLine: string[],
    optionField: keyof typeof lineOptionMap,
) {
    if (markupPerLine.some((i) => i.includes(optionField))) {
        markupPerLine = markupPerLine.map((i) => {
            if (i.includes('SALE_LINE_ITEMS')) {
                return `${i}${lineOptionMap[optionField]}`
            }
            return i
        })
        markupPerLine = removeFieldFromTemplate(markupPerLine, [optionField])
    }
    return markupPerLine
}

const refundLineOptionMap = {
    REFUNDED_LINE_ITEM_SPEC: '+extend',
    REFUNDED_LINE_ITEM_PRICE: '+price',
}

function addRefundLineOption(
    markupPerLine: string[],
    optionField: keyof typeof refundLineOptionMap,
) {
    if (markupPerLine.some((i) => i.includes(optionField))) {
        markupPerLine = markupPerLine.map((i) => {
            if (i.includes('REFUNDED_LINE_ITEMS')) {
                return `${i}${refundLineOptionMap[optionField]}`
            }
            return i
        })
        markupPerLine = removeFieldFromTemplate(markupPerLine, [optionField])
    }
    return markupPerLine
}

const discountOptionMap = {
    DISCOUNTS_DETAIL: '+details',
}
function addDiscountOption(
    markupPerLine: string[],
    optionField: keyof typeof discountOptionMap,
) {
    if (markupPerLine.some((i) => i.includes(optionField))) {
        markupPerLine = markupPerLine.map((i) => {
            if (i.includes('DISCOUNTS')) {
                return `${i}${discountOptionMap[optionField]}`
            }
            return i
        })
        markupPerLine = removeFieldFromTemplate(markupPerLine, [optionField])
    }
    return markupPerLine
}
const paymentOptionMap = {
    PAYMENT_CHANGE: '+change',
    PAYMENT_AMOUNT: '+amount',
    GIFT_CARD_REMAINING_BALANCE: '+giftcardBalance',
}
